
import { useState } from 'react';

export default function CalculatorSection() {
  const [homeValue, setHomeValue] = useState('');
  const [mortgageBalance, setMortgageBalance] = useState('');
  
  const formatCurrency = (value: string) => {
    const num = parseFloat(value.replace(/[,$]/g, ''));
    if (isNaN(num)) return '$0';
    return '$' + num.toLocaleString();
  };

  const calculateMaxInvestment = () => {
    const home = parseFloat(homeValue.replace(/[,$]/g, '')) || 0;
    const mortgage = parseFloat(mortgageBalance.replace(/[,$]/g, '')) || 0;
    const maxInvestment = Math.max(0, home * 0.7 - mortgage);
    return maxInvestment;
  };

  const maxInvestment = calculateMaxInvestment();
  const seventyPercentValue = parseFloat(homeValue.replace(/[,$]/g, '')) * 0.7 || 0;

  const handleValueChange = (value: string, setter: (value: string) => void) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue) {
      const formatted = parseInt(numericValue).toLocaleString();
      setter(formatted);
    } else {
      setter('');
    }
  };

  return (
    <section className="w-full scroll-mt-10 bg-[#f8f8f0]" id="calculator-section">
      <div className="container mx-auto py-8 md:py-16 px-12">
        <div>
          <h2 className="text-center text-3xl md:text-5xl 2xl:text-6xl font-semibold tracking-tight text-blue-900">
            BTIP calculator
          </h2>
        </div>
        <p className="text-center text-xl md:text-3xl text-orange-500 max-w-3xl mx-auto md:mt-2 origin-center">
          Simulate possible outcomes in 3 steps
        </p>
        
        <div className="mt-10 md:mt-20 prose dark:prose-invert max-w-none">
          <div className="bg-white border-1 border-orange-500 max-w-4xl mx-auto flex flex-col gap-4 rounded-2xl p-5 md:p-8 shadow-[0px_5px_35px_-1px_rgba(52,31,0,0.3)]">
            <div className="flex flex-col">
              <div>
                <h3 className="text-lg md:text-2xl 2xl:text-3xl font-bold leading-tight">
                  First, let's calculate the max investment you qualify for.
                </h3>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="col-span-1">
                    <div className="text-sm font-medium mb-2">Property value</div>
                    <input
                      className="bg-gradient-to-b from-[#f9f9f9] to-[#fafafa] shadow-[inset_0px_0px_1px_0_rgba(0,0,0,.1)] h-9 md:h-12 text-base md:text-xl placeholder-[#ccc] p-1 md:p-2 rounded-lg border border-gray-300 w-full focus-border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                      placeholder="Current value"
                      value={homeValue}
                      onChange={(e) => handleValueChange(e.target.value, setHomeValue)}
                    />
                  </div>
                  <div className="col-span-1">
                    <div className="text-sm font-medium mb-2">Mortgage balance</div>
                    <input
                      className="bg-gradient-to-b from-[#f9f9f9] to-[#fafafa] shadow-[inset_0px_0px_1px_0_rgba(0,0,0,.1)] h-9 md:h-12 text-base md:text-xl placeholder-[#ccc] p-1 md:p-2 rounded-lg border border-gray-300 w-full focus-border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                      placeholder="How much you owe"
                      value={mortgageBalance}
                      onChange={(e) => handleValueChange(e.target.value, setMortgageBalance)}
                    />
                  </div>
                </div>
              </div>
              
              <div className="transition-opacity duration-300 ease-in-out opacity-100">
                <div className="mt-5 flex flex-col px-2">
                  <div className="flex flex-row items-center justify-center mt-1 gap-1.5 md:gap-3">
                    <div className="text-center flex flex-col justify-center">
                      <div className="text-gray-500 text-xs tracking-normal md:text-sm flex items-center justify-center gap-1">
                        70% of value
                      </div>
                      <div className="text-base md:text-3xl 2xl:text-4xl font-bold">
                        {formatCurrency(seventyPercentValue.toString())}
                      </div>
                    </div>
                    <div className="text-center flex flex-col justify-center">
                      <div className="text-gray-500 text-xs tracking-normal md:text-sm flex items-center justify-center gap-1"> </div>
                      <div className="text-base md:text-3xl 2xl:text-4xl font-bold">-</div>
                    </div>
                    <div className="text-center flex flex-col justify-center">
                      <div className="text-gray-500 text-xs tracking-normal md:text-sm flex items-center justify-center gap-1">
                        <div className="w-2 h-2 rounded-xs bg-red-800"></div>
                        <span className="hidden sm:inline text-nowrap">Mortgage balance</span>
                        <span className="inline sm:hidden text-nowrap">Mortgage bal.</span>
                      </div>
                      <div className="text-base md:text-3xl 2xl:text-4xl font-bold text-red-800">
                        {formatCurrency(mortgageBalance)}
                      </div>
                    </div>
                    <div className="text-center flex flex-col justify-center">
                      <div className="text-gray-500 text-xs tracking-normal md:text-sm flex items-center justify-center gap-1"> </div>
                      <div className="text-base md:text-3xl 2xl:text-4xl font-bold">=</div>
                    </div>
                    <div className="text-center flex flex-col justify-center border-1 md:border-3 border-orange-500 p-1 md:p-3 rounded-md shadow-[0px_0px_30px_5px_rgba(255,150,100,.2)]">
                      <div className="text-gray-500 text-xs tracking-normal md:text-sm flex items-center justify-center gap-1">
                        <div className="w-2 h-2 rounded-xs bg-orange-500"></div>Max investment
                      </div>
                      <div className="text-base md:text-3xl 2xl:text-4xl font-bold text-orange-500">
                        {formatCurrency(maxInvestment.toString())}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-center mb-2 text-xs md:text-sm text-gray-500">
                    <p>Max investment is 70% of property value minus mortgage bal.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
